import { useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { navigationIsBlocked } from "@/lib/session-navigation";
import { CourseHomeLoading, CourseHomeUnavailable } from "./CourseHomeOverview";
import { CourseAssignmentSheet } from "./CourseAssignmentSheet";
import { AssignmentDraftGuard } from "@/components/assignments/assignment-draft-guard";
import { CourseHeaderDestinations, CourseViewNavigation } from "./CourseViewNavigation";
import { refusedRead } from "./course-page-boxes";
import { CourseSettingsFrame, CourseTeamFrame } from "./CoursePageFrames";
import { CourseSessionWorkspace } from "@/pages/session/SessionPortalPage";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { assignmentsApi } from "@/lib/api/assignments";
import { courseLearningPlanApi } from "@/lib/api/courseLearningPlan";
import { attendanceApi } from "@/lib/api/attendance";
import type { CourseHomePerspective } from "@/lib/api/courseHome";
import { useCourseHome } from "@/hooks/use-course-home";
import { useCourseSectionTag } from "@/hooks/use-course-section-tag";
import { courseBandMeta } from "./course-band-meta";
import { courseWorkspacePath, type CourseWorkspaceShell } from "@/navigation/routes";
import { resolveCourseArrival } from "@/navigation/course-address";
import { courseCalendarHref, type CourseWorkspaceView } from "@/navigation/course-workspace";
import { courseViewRow } from "@/navigation/course-views";
import { FRAME_COPY } from "./course-frame-copy";
import { cameFromDesk } from "./desk/desk-memory";
import { CourseDesk } from "./CourseDesk";
import { CourseBand, CourseFrameShell, PAGE } from "./course-frame";
import { focusFrameHeading, useVisitOpen } from "./use-frame-focus";
import { addressedPart } from "./course-part";

interface CourseWorkspaceProps {
  courseId: string;
  perspective: CourseHomePerspective;
  canEdit: boolean;
  portalShell: CourseWorkspaceShell;
  portalEnabled: boolean;
  workspaceHref: string;
}

/**
 * The course workspace: one frame (R6) around Desk, the exact session and the
 * addressed views (#1393 M4). Every arrival first passes the one resolver
 * (`course-address.ts`): an older address is replaced once by its canonical
 * address before any owner draws, an invalid one says so, and a canonical one
 * renders its family. The leave guard sits above the switch, so one guard
 * instance coordinates leaving across them.
 */
export function CourseWorkspace(props: CourseWorkspaceProps) {
  const [params] = useSearchParams();
  const location = useLocation();
  const teaching = props.perspective === "teaching";
  const arrival = resolveCourseArrival(teaching, params);
  useFindSessionShortcut(props, params);
  const family = arrival.kind !== "canonical" ? null : arrival.family === "session" && !props.portalEnabled ? "desk" : arrival.family;
  // One sheet serves the Desk and a session, so it stays mounted while a
  // learner's bare work gains its session (R35, R60).
  const work = useAssignmentSheet(props.courseId, props.perspective, params, family === "desk" || family === "session");
  useLearnerWorkSession(props.courseId, teaching, params, family === "desk" && Boolean(work.projection));
  let body: ReactNode;
  if (arrival.kind === "rewrite") {
    // Rule 1: the arrival entry is replaced once, with its hash and state, and nothing is drawn for it.
    body = <Navigate replace to={{ pathname: location.pathname, search: arrival.search ? `?${arrival.search}` : "", hash: location.hash }} state={location.state} />;
  } else if (arrival.kind === "invalid") {
    body = <CourseViewUnavailable {...props} />;
  } else if (arrival.family === "view") {
    body = <CourseWorkspaceContent {...props} />;
  } else if (family === "session") {
    body = <CourseExactSession {...props} work={work} />;
  } else {
    body = <CourseDeskPage {...props} work={work} />;
  }
  return <AssignmentDraftGuard>{body}{work.sheet}</AssignmentDraftGuard>;
}

/**
 * Ctrl/Cmd-K finds a session: Desk's Calendar order with Find focused. The
 * selected session stays as the row's context. Arriving there again replaces,
 * so repeated presses add no history.
 */
function useFindSessionShortcut({ courseId, portalShell, portalEnabled }: CourseWorkspaceProps, params: URLSearchParams) {
  const navigate = useNavigate();
  const location = useLocation();
  const session = params.getAll("session").length === 1 ? params.get("session") : null;
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!portalEnabled || navigationIsBlocked(event)
        || event.altKey || event.shiftKey || event.key.toLowerCase() !== "k"
        || event.metaKey === event.ctrlKey) return;
      event.preventDefault();
      const target = courseCalendarHref(courseWorkspacePath(portalShell, courseId), { session });
      const here = `${location.pathname}${location.search}` === target;
      navigate(target, { replace: here, state: { focusSessionFind: true } });
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [portalEnabled, portalShell, courseId, session, navigate, location.pathname, location.search]);
}

/** Rule 4: an address that fails validation is never guessed at. It needs no course read. */
function CourseViewUnavailable({ courseId, perspective, portalShell, workspaceHref }: CourseWorkspaceProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const visitOpen = useVisitOpen();
  useEffect(() => focusFrameHeading(heading.current, visitOpen), [visitOpen]);
  return <CourseFrameShell perspective={perspective} workspaceHref={workspaceHref}>
    <div role="alert" className={PAGE}>
      <header data-slot="page-frame" className="grid min-w-0 items-end gap-5 lg:grid-cols-[minmax(0,1fr)_auto]">
        <h1 ref={heading} tabIndex={-1} className="text-title font-semibold text-foreground outline-none">
          Course view unavailable
        </h1>
        <Button asChild variant="outline">
          <Link to={courseWorkspacePath(portalShell, courseId)}>Return to Desk</Link>
        </Button>
      </header>
    </div>
  </CourseFrameShell>;
}

/**
 * Exact work outside the Coursework register opens as a sheet over the page:
 * a learner's work (R35, R60) and a Classroom record (R36). The sheet needs
 * the course projection, read only while an address names work; once read it
 * stays mounted across Return, so it can restore the initiating control.
 */
function useAssignmentSheet(courseId: string, perspective: CourseHomePerspective, params: URLSearchParams, active: boolean) {
  const assignment = active && params.has("assignment");
  const courseHome = useCourseHome(courseId, perspective, assignment);
  const projection = active ? courseHome.data : undefined;
  // An address that names work says so while its read is not ready: the page
  // behind stays, and the work is never silently absent (#1393 M4-A2).
  const state = !assignment || projection ? null : courseHome.isNoProjection ? "refused" : courseHome.isError ? "failed" : "loading";
  return {
    hidden: Boolean(projection && assignment),
    sheet: projection ? <CourseAssignmentSheet projection={projection} /> : null,
    status: state && <WorkArrivalStatus state={state} retry={() => void courseHome.refetch()} />,
    projection,
  };
}
type AssignmentSheet = ReturnType<typeof useAssignmentSheet>;

function WorkArrivalStatus({ state, retry }: { state: "loading" | "failed" | "refused"; retry(): void }) {
  if (state === "loading") return <p role="status" data-work-arrival="loading" className="text-ui text-muted-foreground">{FRAME_COPY.work.loading}</p>;
  return <div role="alert" data-work-arrival={state} className="flex flex-wrap items-center gap-3 text-ui">
    <p>{state === "failed" ? FRAME_COPY.work.failed : FRAME_COPY.work.refused}</p>
    {state === "failed" && <Button variant="outline" onClick={retry}>{FRAME_COPY.work.retry}</Button>}
  </div>;
}

/**
 * A learner's bare work names no session (R35, R60): the frame reads the
 * work's unit and, when it maps to a session, replaces the arrival with that
 * session as context. Pending or failed leaves the address as it is (rule 6).
 */
function useLearnerWorkSession(courseId: string, teaching: boolean, params: URLSearchParams, ready: boolean) {
  const { me } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const assignmentId = params.get("assignment");
  const native = !params.has("owner") || params.get("owner") === "native";
  const read = useQuery({
    queryKey: ["learner-assignment-session", me?.tenant_id, me?.user_id, courseId, assignmentId],
    enabled: Boolean(ready && !teaching && assignmentId && !params.has("session") && native),
    retry: false,
    queryFn: async () => {
      const assignment = await assignmentsApi.get(assignmentId!);
      if (assignment.id !== assignmentId || assignment.course_id !== courseId || assignment.status === "draft") return null;
      if (!assignment.unit_id) return null;
      const plan = await courseLearningPlanApi.get(courseId);
      return plan.course_id === courseId ? plan.units.find(unit => unit.id === assignment.unit_id)?.session_id ?? null : null;
    },
  });
  useEffect(() => {
    if (!ready || teaching || !read.data || params.has("session") || !params.has("assignment")) return;
    const next = new URLSearchParams(params);
    next.set("session", read.data);
    navigate({ search: next.toString(), hash: location.hash }, { replace: true, state: location.state });
  }, [ready, teaching, read.data, params, navigate, location.hash, location.state]);
}

/** Desk: the bare course, its Calendar order and its Resources part. */
function CourseDeskPage({ courseId, perspective, canEdit, portalShell, workspaceHref, work: { hidden, status } }: CourseWorkspaceProps & { work: AssignmentSheet }) {
  // Closing a sheet hands focus back to the Desk's heading, as a view's frame does.
  const page = useRef<HTMLDivElement>(null);
  const wasHidden = useRef(hidden);
  const visitOpen = useVisitOpen();
  useEffect(() => {
    if (wasHidden.current && !hidden) focusFrameHeading(page.current?.querySelector("h1") ?? null, visitOpen);
    wasHidden.current = hidden;
  }, [hidden, visitOpen]);
  return <CourseFrameShell perspective={perspective} workspaceHref={workspaceHref} hidden={hidden}>
    <div ref={page} className={PAGE}>
      {status}
      <CourseDesk key={`${courseId}:${perspective}`} courseId={courseId} perspective={perspective} backHref={workspaceHref} courseHref={courseWorkspacePath(portalShell, courseId)} canEdit={canEdit} shell={portalShell} />
    </div>
  </CourseFrameShell>;
}

/**
 * One session of the course (`?session=S`). An addressed occurrence has its
 * own authorized read and identity checks: a course overview failure never
 * withdraws it, its neighbours, or Return. It opens once this course's list
 * holds it, so an address outside the course never reads that session (C01).
 */
function CourseExactSession({ courseId, perspective, portalShell, workspaceHref, work: { hidden, status } }: CourseWorkspaceProps & { work: AssignmentSheet }) {
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const teaching = perspective === "teaching";
  const homeHref = courseWorkspacePath(portalShell, courseId);
  const selectedId = params.get("session")!;
  const sessions = useQuery({
    queryKey: ["course-workspace-sessions", me?.tenant_id, me?.user_id, courseId],
    queryFn: () => attendanceApi.listSessions(courseId),
    retry: false,
  });
  const refused = refusedRead(sessions.error);
  const rows = !refused && Array.isArray(sessions.data) ? sessions.data.filter(row => row.course_id === courseId) : [];
  const invalid = refused || sessions.isSuccess && !rows.some(row => row.id === selectedId);
  const open = !invalid && (sessions.isError || rows.some(row => row.id === selectedId));
  const heading = useRef<HTMLHeadingElement>(null);
  const visitOpen = useVisitOpen();
  useEffect(() => {
    if (invalid) focusFrameHeading(heading.current, visitOpen);
  }, [invalid, visitOpen]);
  const selectSession = (id: string) => {
    const next = new URLSearchParams(params);
    for (const key of ["view", "unit", "class", "plan", "assignment", "submission", "notice", "compose", "open", "owner", "change"]) next.delete(key);
    next.set("session", id);
    setParams(next);
  };
  const showSessions = () => navigate(courseCalendarHref(homeHref, { session: selectedId }));
  const unavailable = (retry?: () => void) => <div className={PAGE}>
    <h1 ref={heading} tabIndex={-1} className="text-title font-semibold outline-none">{FRAME_COPY.exactSession.unavailable}</h1>
    <p role="alert">{retry ? FRAME_COPY.exactSession.failed : FRAME_COPY.exactSession.refused}</p>
    {retry && <Button variant="outline" onClick={retry}>{FRAME_COPY.exactSession.retry}</Button>}
  </div>;
  return (
    <CourseFrameShell perspective={perspective} workspaceHref={workspaceHref} home={false} hidden={hidden}>
      {/* Return steps Back to this course's Desk when the session was opened from it, so the Desk is as it was left; otherwise it opens a fresh Desk (#1393 contract 4). */}
      <Link to={homeHref} onClick={event => {
        if (!cameFromDesk(location.state, courseId) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        navigate(-1);
      }} className="mx-4 inline-flex min-h-11 items-center text-ui text-primary underline-offset-4 hover:underline sm:mx-7">{FRAME_COPY.exactSession.return}</Link>
      {status && <div className="px-4 sm:px-7">{status}</div>}
      {invalid ? unavailable() : open ? <CourseSessionWorkspace
        key={`${courseId}:${selectedId}`}
        courseId={courseId}
        perspective={perspective}
        sessionId={selectedId}
        shell={portalShell}
        sessions={rows}
        courseViews={<CourseViewNavigation courseHref={homeHref} selectedSessionId={selectedId} teaching={teaching} />}
        onSelectSession={selectSession}
        onShowSessions={showSessions}
        unreadSession={failure => unavailable(failure.kind === "failed" ? failure.retry : undefined)}
      /> : <p role="status" className="px-4 text-ui text-muted-foreground sm:px-7">{FRAME_COPY.exactSession.loading}</p>}
    </CourseFrameShell>
  );
}

/** An addressed view: Coursework, Gradebook or Grades, People, Manage course, All notices. */
function CourseWorkspaceContent({
  courseId,
  perspective,
  canEdit,
  portalShell,
  workspaceHref,
}: CourseWorkspaceProps) {
  const { me } = useAuth();
  const [params] = useSearchParams();
  const teaching = perspective === "teaching";
  const sectionTag = useCourseSectionTag(courseId, teaching);
  const courseHome = useCourseHome(courseId, perspective);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The edition band owns its `h1` (ADR 0035); the frame focuses it on arrival
  // through the band's wrapper, so teaching views keep one focus rule.
  const bandRef = useRef<HTMLDivElement>(null);
  const visitOpen = useVisitOpen();
  const homeHref = courseWorkspacePath(portalShell, courseId);
  const section = params.get("view") as CourseWorkspaceView;
  const viewRow = courseViewRow(section, teaching);
  const projection = courseHome.data;
  const hasProjection = Boolean(projection);
  // Exact native work opens in the Coursework register's row (ADR 0035): a
  // teacher's when the address names no owner, and a learner's when it names
  // Coursework. Classroom-owned records keep their own sheet.
  const nativeOwner = !params.has("owner") || params.get("owner") === "native";
  const courseworkRow = teaching ? !params.has("owner") : nativeOwner && section === "coursework";
  const hasSheet = hasProjection && params.has("assignment") && !courseworkRow;
  // The selected session is context a view may read; it is never exact here.
  const sessionId = params.get("session");
  // An addressed part this view draws owns arrival focus (#1393 R1, R2): its
  // own heading, focused by the part before this parent effect runs.
  const part = addressedPart(params);
  const partFocused = Boolean(hasProjection && part && viewRow?.parts?.({ teaching, canEdit }).some(name => name === part));
  // An open visit owns focus (R6): its heading on entry, its opener on Return.
  // A refusal the view drew in this same commit (a stored link to a notice
  // that is gone) has already focused its sentence (CASE-022) and keeps it.
  useEffect(() => {
    if (partFocused || hasSheet || document.activeElement?.closest("[data-page-refusal]")) return;
    focusFrameHeading(headingRef.current ?? bandRef.current?.querySelector("h1") ?? null, visitOpen);
  }, [visitOpen, courseId, section, courseHome.isPending, hasProjection, hasSheet, partFocused]);
  if (courseHome.isPending) return <CourseHomeLoading />;
  if (!projection)
    return (
      <CourseHomeUnavailable
        noProjection={courseHome.isNoProjection || (courseHome.error instanceof ApiError && courseHome.error.status === 404)}
        onRetry={() => void courseHome.refetch()}
        backHref={workspaceHref}
        headingRef={headingRef}
      />
    );
  // The frame's own course-page boxes in a view: the learner's teaching team
  // and Manage course's jobs.
  let frameContent: ReactNode = null;
  if (section === "people" && !teaching) {
    frameContent = <CourseTeamFrame courseId={courseId} bandName={sectionTag?.startsWith("CRN ") ? null : sectionTag} />;
  } else if (section === "settings") {
    frameContent = (
      <>
        <CourseSettingsFrame courseId={courseId} projection={projection} canEditDetails={me?.role === "admin" || canEdit} registrar={me?.role === "admin"} />
      </>
    );
  }
  return (
    <>
      <CourseFrameShell
        perspective={perspective}
        workspaceHref={workspaceHref}
        hidden={hasSheet}
        notice={courseHome.isRefetchError && (
          <p role="status" className="px-4 pt-4 text-ui text-muted-foreground sm:px-7">
            Showing the last confirmed course. Refresh did not complete.
          </p>
        )}
      >
        <div className={PAGE}>
          <CourseBand
            bandRef={bandRef}
            code={projection.course.code}
            eyebrow={projection.course.term?.name}
            title={projection.course.title}
            meta={courseBandMeta(projection.course, sectionTag)}
            action={<CourseHeaderDestinations courseHref={homeHref} teaching={teaching} activeView={section} />}
          />
          <CourseViewNavigation courseHref={homeHref} selectedSessionId={sessionId} teaching={teaching} activeView={section} />
          {viewRow?.render({
            courseId,
            projection,
            teaching,
            canEdit,
            onRetryProjection: () => void courseHome.refetch(),
            frame: frameContent,
          })}
        </div>
      </CourseFrameShell>
      <CourseAssignmentSheet projection={projection} />
    </>
  );
}
